import { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  FolderKanban,
  MessageSquarePlus,
  Settings,
  ChevronLeft,
  Plus,
  Users,
  Bot,
  MessageSquare,
  Bell,
  LogOut,
  User,
  Check,
  CheckCheck,
  Trash2,
  ClipboardList,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuthStore } from '@/stores/authStore';
import { useUIStore } from '@/stores/uiStore';
import { useNotificationStore } from '@/stores/notificationStore';
import { useChatStore } from '@/stores/chatStore';
import { useRecentChatSessions } from '@/hooks/useChatSessions';
import { useAuth } from '@/hooks/useAuth';
import { getInitials, truncate, formatRelativeTime } from '@/lib/utils';
import type { Notification, NotificationType } from '@/types';

function getNotificationIcon(type: NotificationType) {
  switch (type) {
    case 'task_assigned':
    case 'task_status_changed':
    case 'task_completed':
    case 'task_due_soon':
    case 'task_overdue':
      return ClipboardList;
    case 'message_received':
    case 'task_mentioned':
    case 'task_comment':
      return MessageSquare;
    case 'project_invitation':
    case 'project_role_changed':
      return FolderKanban;
    default:
      return Bell;
  }
}

function getNotificationColor(type: NotificationType): string {
  switch (type) {
    case 'task_overdue':
      return 'text-red-500';
    case 'task_due_soon':
      return 'text-orange-500';
    case 'task_completed':
      return 'text-green-500';
    case 'task_assigned':
      return 'text-blue-500';
    case 'project_invitation':
      return 'text-purple-500';
    default:
      return 'text-muted-foreground';
  }
}

function NotificationItem({
  notification,
  onRead,
  onDelete,
  onClick,
}: {
  notification: Notification;
  onRead: () => void;
  onDelete: () => void;
  onClick: () => void;
}) {
  const Icon = getNotificationIcon(notification.type);
  const iconColor = getNotificationColor(notification.type);

  return (
    <div
      className={cn(
        'flex items-start gap-3 p-3 hover:bg-muted/50 cursor-pointer transition-colors',
        !notification.read && 'bg-muted/30'
      )}
      onClick={onClick}
    >
      <div className={cn('mt-0.5', iconColor)}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className={cn('text-sm line-clamp-1', !notification.read && 'font-medium')}>
              {notification.title}
            </p>
            <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
              {notification.message}
            </p>
            <div className="flex items-center gap-2 mt-1">
              {notification.project && (
                <span className="text-xs text-muted-foreground">
                  {notification.project.name}
                </span>
              )}
              <span className="text-xs text-muted-foreground">
                {formatRelativeTime(notification.createdAt)}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {!notification.read && (
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={(e) => {
                  e.stopPropagation();
                  onRead();
                }}
                title="읽음 처리"
              >
                <Check className="h-3 w-3" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-muted-foreground hover:text-destructive"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              title="삭제"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </div>
      {!notification.read && (
        <div className="w-2 h-2 rounded-full bg-primary shrink-0 mt-1.5" />
      )}
    </div>
  );
}

export function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();
  const { sidebarOpen, toggleSidebar, openModal } = useUIStore();
  const { sessions: recentChats, isLoading: isLoadingChats } = useRecentChatSessions(5);
  const { logout } = useAuth();

  const {
    notifications,
    unreadCount,
    isLoading: isLoadingNotifications,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    connect,
    disconnect,
  } = useNotificationStore();

  // Connect to WebSocket when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      connect();
      fetchUnreadCount();
      fetchNotifications(true);
    }

    return () => {
      disconnect();
    };
  }, [isAuthenticated, connect, disconnect, fetchUnreadCount, fetchNotifications]);

  const handleNewChat = () => {
    // 채팅 스토어의 활성 세션 초기화 (화면 초기화를 위해)
    useChatStore.getState().setActiveSession(null);
    // 새 채팅 페이지로 이동 - 세션은 첫 메시지 전송 시 생성됨
    navigate('/chat');
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }

    if (notification.notifiable) {
      if (notification.notifiable.type === 'Task' && notification.project) {
        navigate(`/projects/${notification.project.id}?tab=tasks`);
      } else if (notification.notifiable.type === 'Message' && notification.project) {
        navigate(`/projects/${notification.project.id}`);
      } else if (notification.project) {
        navigate(`/projects/${notification.project.id}`);
      }
    }
  };

  return (
    <TooltipProvider>
      <aside
        className={cn(
          'fixed left-0 top-0 z-40 h-screen border-r bg-background transition-all duration-300',
          sidebarOpen ? 'w-64' : 'w-16'
        )}
      >
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="flex h-16 items-center justify-between border-b px-4">
            {sidebarOpen && (
              <Link to="/" className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
                  <span className="text-sm font-bold text-primary-foreground">P</span>
                </div>
                <span className="font-semibold">플레이그라운드</span>
              </Link>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleSidebar}
              className={cn(!sidebarOpen && 'mx-auto')}
            >
              <ChevronLeft
                className={cn(
                  'h-4 w-4 transition-transform',
                  !sidebarOpen && 'rotate-180'
                )}
              />
            </Button>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 px-2 py-4">
            <nav className="space-y-2">
              {/* Dashboard */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link
                    to="/dashboard"
                    className={cn(
                      'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
                      location.pathname === '/dashboard'
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                      !sidebarOpen && 'justify-center'
                    )}
                  >
                    <FolderKanban className="h-5 w-5 shrink-0" />
                    {sidebarOpen && <span>대시보드</span>}
                  </Link>
                </TooltipTrigger>
                {!sidebarOpen && (
                  <TooltipContent side="right">대시보드</TooltipContent>
                )}
              </Tooltip>

              {/* New Chat */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={handleNewChat}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
                      location.pathname === '/chat'
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                      !sidebarOpen && 'justify-center'
                    )}
                  >
                    <MessageSquarePlus className="h-5 w-5 shrink-0" />
                    {sidebarOpen && <span>새 채팅</span>}
                  </button>
                </TooltipTrigger>
                {!sidebarOpen && (
                  <TooltipContent side="right">새 채팅</TooltipContent>
                )}
              </Tooltip>
            </nav>

            {sidebarOpen && (
              <>
                <Separator className="my-4" />
                <div className="space-y-2">
                  <div className="flex items-center justify-between px-3">
                    <span className="text-xs font-medium text-muted-foreground">
                      최근 채팅
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => openModal('chatHistory')}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  {isLoadingChats ? (
                    <div className="px-3 py-2">
                      <div className="h-4 w-full animate-pulse rounded bg-muted" />
                    </div>
                  ) : recentChats.length > 0 ? (
                    recentChats.map((chat) => (
                      <Link
                        key={chat.id}
                        to={`/chat?session=${chat.id}`}
                        className={cn(
                          'flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors',
                          location.search.includes(chat.id)
                            ? 'bg-accent text-accent-foreground'
                            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                        )}
                      >
                        <MessageSquare className="h-4 w-4 shrink-0" />
                        <div className="flex-1 overflow-hidden">
                          <span className="block truncate">
                            {truncate(chat.title || '새 채팅', 18)}
                          </span>
                          <span className="block text-xs text-muted-foreground">
                            {formatRelativeTime(chat.updatedAt)}
                          </span>
                        </div>
                      </Link>
                    ))
                  ) : (
                    <p className="px-3 text-xs text-muted-foreground">
                      아직 채팅이 없습니다
                    </p>
                  )}
                </div>
              </>
            )}
          </ScrollArea>

          {/* Footer */}
          <div className="border-t p-2">
            {/* Admin menu - only visible to admin users */}
            {user?.role === 'admin' && (
              <>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      to="/admin/users"
                      className={cn(
                        'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
                        location.pathname === '/admin/users'
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                        !sidebarOpen && 'justify-center'
                      )}
                    >
                      <Users className="h-5 w-5 shrink-0" />
                      {sidebarOpen && <span>사용자 관리</span>}
                    </Link>
                  </TooltipTrigger>
                  {!sidebarOpen && (
                    <TooltipContent side="right">사용자 관리</TooltipContent>
                  )}
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      to="/admin/llm"
                      className={cn(
                        'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
                        location.pathname === '/admin/llm'
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                        !sidebarOpen && 'justify-center'
                      )}
                    >
                      <Bot className="h-5 w-5 shrink-0" />
                      {sidebarOpen && <span>LLM 설정</span>}
                    </Link>
                  </TooltipTrigger>
                  {!sidebarOpen && (
                    <TooltipContent side="right">LLM 설정</TooltipContent>
                  )}
                </Tooltip>
              </>
            )}

            {/* Notifications */}
            <DropdownMenu>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild>
                    <button
                      className={cn(
                        'relative flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground',
                        !sidebarOpen && 'justify-center'
                      )}
                    >
                      <div className="relative">
                        <Bell className="h-5 w-5 shrink-0" />
                        {unreadCount > 0 && (
                          <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[10px] font-medium text-destructive-foreground">
                            {unreadCount > 9 ? '9+' : unreadCount}
                          </span>
                        )}
                      </div>
                      {sidebarOpen && <span>알림</span>}
                    </button>
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                {!sidebarOpen && (
                  <TooltipContent side="right">알림</TooltipContent>
                )}
              </Tooltip>
              <DropdownMenuContent className="w-80" side="right" align="end">
                <div className="flex items-center justify-between px-3 py-2">
                  <DropdownMenuLabel className="p-0 font-semibold">
                    알림
                    {unreadCount > 0 && (
                      <span className="ml-2 text-xs font-normal text-muted-foreground">
                        {unreadCount}개의 새 알림
                      </span>
                    )}
                  </DropdownMenuLabel>
                  {unreadCount > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-auto px-2 py-1 text-xs"
                      onClick={() => markAllAsRead()}
                    >
                      <CheckCheck className="h-3 w-3 mr-1" />
                      모두 읽음
                    </Button>
                  )}
                </div>
                <DropdownMenuSeparator />
                <ScrollArea className="h-[300px]">
                  {isLoadingNotifications && notifications.length === 0 ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
                    </div>
                  ) : notifications.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                      <Bell className="h-8 w-8 mb-2 opacity-50" />
                      <p className="text-sm">알림이 없습니다</p>
                    </div>
                  ) : (
                    <div className="divide-y">
                      {notifications.slice(0, 10).map((notification) => (
                        <NotificationItem
                          key={notification.id}
                          notification={notification}
                          onRead={() => markAsRead(notification.id)}
                          onDelete={() => deleteNotification(notification.id)}
                          onClick={() => handleNotificationClick(notification)}
                        />
                      ))}
                    </div>
                  )}
                </ScrollArea>
                {notifications.length > 0 && (
                  <>
                    <DropdownMenuSeparator />
                    <div className="p-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => navigate('/notifications')}
                      >
                        모든 알림 보기
                      </Button>
                    </div>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            <Separator className="my-2" />

            {/* User Menu */}
            {user && (
              <DropdownMenu>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DropdownMenuTrigger asChild>
                      <button
                        className={cn(
                          'flex w-full items-center gap-3 rounded-lg px-3 py-2 transition-colors hover:bg-accent',
                          !sidebarOpen && 'justify-center'
                        )}
                      >
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={user.avatarUrl} alt={user.name} />
                          <AvatarFallback>{getInitials(user.name)}</AvatarFallback>
                        </Avatar>
                        {sidebarOpen && (
                          <div className="flex-1 overflow-hidden text-left">
                            <p className="truncate text-sm font-medium">{user.name}</p>
                            <p className="truncate text-xs text-muted-foreground">
                              {user.email}
                            </p>
                          </div>
                        )}
                      </button>
                    </DropdownMenuTrigger>
                  </TooltipTrigger>
                  {!sidebarOpen && (
                    <TooltipContent side="right">프로필</TooltipContent>
                  )}
                </Tooltip>
                <DropdownMenuContent className="w-56" side="right" align="end">
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">{user.name}</p>
                      <p className="text-xs leading-none text-muted-foreground">
                        {user.email}
                      </p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      프로필
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/settings" className="flex items-center gap-2">
                      <Settings className="h-4 w-4" />
                      설정
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={logout}
                    className="flex items-center gap-2 text-destructive focus:text-destructive"
                  >
                    <LogOut className="h-4 w-4" />
                    로그아웃
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </aside>
    </TooltipProvider>
  );
}
